"use client";

import { withAdminSessionHeaders } from "@/lib/adminSession";
import { asRecord } from "@/hooks/webPlayerUtils";
import type {
  SpotifyPlayerDevice,
  SpotifyPlayerDevicesResponse,
  SpotifyPlayerStateResponse,
} from "./spotifyWebPlayer.shared";

export type SpotifyAccessTokenResponse = {
  accessToken: string | null;
  payloadError: string | null;
  status: number;
  ok: boolean;
};

export type SpotifyCommandResult = {
  ok: boolean;
  status: number;
  errorMessage: string | null;
  responseText: string;
};

async function sendSpotifyCommand(
  path: string,
  init: RequestInit,
  fallbackMessage: string,
) {
  try {
    const res = await fetch(path, withAdminSessionHeaders(init));
    const responseText = await res.text().catch(() => "");

    return {
      ok: res.ok,
      status: res.status,
      errorMessage: res.ok
        ? null
        : responseText || `${fallbackMessage} (HTTP ${res.status})`,
      responseText,
    } satisfies SpotifyCommandResult;
  } catch (error) {
    return {
      ok: false,
      status: 0,
      errorMessage:
        error instanceof Error ? error.message : fallbackMessage,
      responseText: "",
    } satisfies SpotifyCommandResult;
  }
}

export async function fetchSpotifyAccessToken() {
  const res = await fetch(
    "/api/spotify/access-token",
    withAdminSessionHeaders({
      credentials: "include",
    })
  );
  const payload = (await res.json().catch(() => ({}))) as unknown;
  const payloadRecord = asRecord(payload);
  const accessToken =
    typeof payloadRecord.accessToken === "string"
      ? payloadRecord.accessToken
      : null;
  const payloadError =
    typeof payloadRecord.error === "string" ? payloadRecord.error : null;

  return {
    accessToken,
    payloadError,
    status: res.status,
    ok: res.ok,
  } satisfies SpotifyAccessTokenResponse;
}

export async function fetchSpotifyRemotePlayerState() {
  try {
    const res = await fetch(
      "/api/spotify/player/state",
      withAdminSessionHeaders({
        credentials: "include",
      })
    );
    const payload = (await res.json().catch(() => ({}))) as SpotifyPlayerStateResponse;
    if (!res.ok) {
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}

export async function fetchSpotifyAvailableDevices() {
  try {
    const res = await fetch(
      "/api/spotify/player/devices",
      withAdminSessionHeaders({
        credentials: "include",
      })
    );
    const payload = (await res.json().catch(() => ({}))) as SpotifyPlayerDevicesResponse;
    if (!res.ok) {
      return null;
    }
    return Array.isArray(payload.devices) ? payload.devices : [];
  } catch {
    return null;
  }
}

export async function resumeSpotifyPlaybackOnDevice(playbackDeviceId: string) {
  return startSpotifyPlaybackOnDevice({
    deviceId: playbackDeviceId,
    fallbackMessage: "Failed to resume playback",
  });
}

export async function pauseSpotifyPlayback() {
  return sendSpotifyCommand(
    "/api/spotify/player/pause",
    {
      method: "PUT",
      credentials: "include",
    },
    "Failed to pause playback",
  );
}

export async function transferSpotifyPlayback(
  playbackDeviceId: string,
  play: boolean
) {
  return sendSpotifyCommand(
    "/api/spotify/player/transfer",
    {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: playbackDeviceId, play }),
    },
    "Failed to transfer playback",
  );
}

export async function startSpotifyPlaybackOnDevice({
  deviceId,
  uris,
  positionMs,
  fallbackMessage = "Failed to start playback",
}: {
  deviceId: string;
  uris?: string[];
  positionMs?: number;
  fallbackMessage?: string;
}) {
  const body: Record<string, unknown> = { deviceId };

  if (Array.isArray(uris) && uris.length > 0) {
    body.uris = uris;
  }
  if (typeof positionMs === "number" && Number.isFinite(positionMs)) {
    body.position_ms = Math.max(0, positionMs);
  }

  return sendSpotifyCommand(
    "/api/spotify/play",
    {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    fallbackMessage,
  );
}

export async function playSpotifyTrackSegment({
  deviceId,
  trackUri,
  startMs,
}: {
  deviceId: string;
  trackUri: string;
  startMs: number;
}) {
  return startSpotifyPlaybackOnDevice({
    deviceId,
    uris: [trackUri],
    positionMs: startMs,
    fallbackMessage: "Failed to start Spotify segment playback",
  });
}

export function getUsableSpotifyDevices(devices: SpotifyPlayerDevice[]) {
  return devices.filter(
    (device) =>
      typeof device.id === "string" &&
      device.id.trim().length > 0 &&
      device.isRestricted !== true
  );
}
